import React, { useEffect } from 'react'; // Убраны useState, useRef, useCallback, ReactDOMServer, axios
// ReactDOMServer и axios используются внутри хуков
import ResponsiveComplianceChart from './components/ui/ResponsiveComplianceChart.tsx';
import FoundersFileManager from './components/FoundersFileManager';
// import { exportSvgToPng } from './utils/exportSvgToPng'; // Удаляем, не используется
// import { calculateChartHeight } from './utils/chartUtils'; // Удаляем, не используется
// import { wrapText } from './utils/textUtils'; // Удаляем, не используется
import CustomDatePicker from './components/CustomDatePicker';
import AnalysisProgressDisplay from './components/AnalysisProgressDisplay'; // <-- Импорт нового компонента
import ExportStatusIndicator from './components/ExportStatusIndicator'; // <-- Импорт компонента индикатора экспорта
import { useAnalysisParameters } from './hooks/useAnalysisParameters';
import { useAnalysisProcess } from './hooks/useAnalysisProcess'; 
import { useExport } from './hooks/useExport'; // <-- Импорт нового хука
import { processChartData } from './utils/chartDataProcessor'; // <-- Импорт обработчика данных
import { useAuth } from './contexts/AuthContext';
import LoginForm from './components/Auth/LoginForm';

const MONTH_OPTIONS = [
  { value: '01', label: 'Январь' },
  { value: '02', label: 'Февраль' },
  { value: '03', label: 'Март' },
  { value: '04', label: 'Апрель' },
  { value: '05', label: 'Май' },
  { value: '06', label: 'Июнь' },
  { value: '07', label: 'Июль' },
  { value: '08', label: 'Август' },
  { value: '09', label: 'Сентябрь' },
  { value: '10', label: 'Октябрь' },
  { value: '11', label: 'Ноябрь' },
  { value: '12', label: 'Декабрь' },
];

function App() {
  const { user, loading: authLoading, signOutUser } = useAuth();

  const {
    foundersFile,
    startDate,
    endDate,
    selectedMonth,
    postThreshold,
    analysisMode,
    analysisError,
    setAnalysisError,
    handleFoundersUpload,
    handleStartDateChange,
    handleEndDateChange,
    handleSelectedMonthChange,
    handlePostThresholdChange,
    handleAnalysisModeChange,
  } = useAnalysisParameters();
      
      const { 
    isLoading,
    progress,
    statusMessage,
    setStatusMessage,
    processingStage,
    stageDetails,
        estimatedTimeLeft, 
    elapsedTime,
    processingSpeed,
    currentItem,
    analysisEndTime,
    vkAnalysisResult,
    fetchDataFromVK,
    handleStopAnalysis,
  } = useAnalysisProcess({ foundersFile, startDate, endDate, postThreshold, analysisMode }, setAnalysisError);

  const {
    exportOmsuChart,
    exportIogvChart,
    handleExportOmsuExcel,
    handleExportIogvExcel,
    handleExportAll,
    exportStatus,  // <-- Получаем статус экспорта из хука
  } = useExport(
    { vkAnalysisResult, analysisEndTime }, 
    { startDate, endDate, postThreshold, analysisMode }, 
    setAnalysisError,   // Для критических ошибок экспорта
    setStatusMessage,  // Для информационных сообщений от экспорта
    statusMessage     // Текущее значение statusMessage
  );

  // Подготовка данных для UI графиков
  let processedOmsuForUI, processedIogvForUI;
  if (vkAnalysisResult) {
    // Для ОМСУ
    if (vkAnalysisResult.results && vkAnalysisResult.results.omsu) {
      processedOmsuForUI = processChartData(
        vkAnalysisResult.results.omsu, 
        vkAnalysisResult.results.omsu.totalViolations,
        vkAnalysisResult.results.omsu.compliantFounders,
        'ОМСУ', 
        { startDate, endDate, analysisMode, analysisMetadata: vkAnalysisResult.analysisMetadata },
        analysisEndTime
      );
    }
    // Для ИОГВ/ИОМО
    if (vkAnalysisResult.results && vkAnalysisResult.results.iogv) {
      processedIogvForUI = processChartData(
        vkAnalysisResult.results.iogv, 
        vkAnalysisResult.results.iogv.totalViolations,
        vkAnalysisResult.results.iogv.compliantFounders,
        'ИОМО', // Используем 'ИОМО' для заголовка в UI, как и раньше
        { startDate, endDate, analysisMode, analysisMetadata: vkAnalysisResult.analysisMetadata },
        analysisEndTime
      );
    }
  }

  useEffect(() => {
    const consoleStyles = [
      'color: #007bff', 
      'font-weight: bold',
      'font-size: 14px'
    ].join(';');
    console.log('%cCompliance Analytics App Initialized', consoleStyles);
  }, []); 

  const selectedMonthMatch = selectedMonth.match(/^(\d{4})-(\d{2})$/);
  const selectedMonthYear = selectedMonthMatch ? selectedMonthMatch[1] : String(new Date().getFullYear());
  const selectedMonthNumber = selectedMonthMatch ? selectedMonthMatch[2] : '01';
  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from(
    { length: currentYear - 2000 + 2 },
    (_, index) => String(currentYear + 1 - index)
  );

  if (authLoading) {
    return (
      <div className="auth-container">
        <div className="auth-card">
          <h2>Загрузка…</h2>
        </div>
      </div>
    );
  }

  if (!user) {
    return <LoginForm />;
  }

  // JSX без изменений, кроме вызовов функций экспорта из хука
  return (
    <div className="app-container">
      <div className="content-container">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <h1 className="page-title m-0">
            Анализ выполнения нормы публикаций в госпабликах
          </h1>
          <button
            onClick={signOutUser}
            className="btn btn-outline"
          >
            Выйти ({user.email})
          </button>
        </div>
        <div className="card">
          <div className="card-section py-3">
            <FoundersFileManager 
              onFoundersFileLoaded={handleFoundersUpload}
              isLoading={isLoading}
            />
          </div>
          <div className="card-section">
             <h3 className="section-header">
                 <span>Данные о публикациях</span>
             </h3>
             <div className="section-content">
                 <div className="flex flex-wrap gap-4 mb-4 items-end">
                     <div className="flex-1 min-w-[300px]">
                         <label className="form-label block mb-2">
                           {analysisMode === 'monthly_4weeks' ? 'Месяц анализа:' : 'Период анализа:'}
                         </label>
                         {analysisMode === 'monthly_4weeks' ? (
                           <div className="flex gap-4 max-w-[420px]">
                             <div className="w-[240px]">
                               <label className="text-xs text-gray-500 mb-1 block">Месяц:</label>
                               <select
                                 value={selectedMonthNumber}
                                 onChange={(e) => handleSelectedMonthChange(`${selectedMonthYear}-${e.target.value}`)}
                                 className="form-control w-full"
                               >
                                 {MONTH_OPTIONS.map(option => (
                                   <option key={option.value} value={option.value}>
                                     {option.label}
                                   </option>
                                 ))}
                               </select>
                             </div>
                             <div className="w-[160px]">
                               <label className="text-xs text-gray-500 mb-1 block">Год:</label>
                               <select
                                 value={selectedMonthYear}
                                 onChange={(e) => handleSelectedMonthChange(`${e.target.value}-${selectedMonthNumber}`)}
                                 className="form-control w-full"
                               >
                                 {yearOptions.map(year => (
                                   <option key={year} value={year}>
                                     {year}
                                   </option>
                                 ))}
                               </select>
                             </div>
                           </div>
                         ) : (
                           <div className="flex gap-4">
                               <div className="w-1/2">
                                   <label className="text-xs text-gray-500 mb-1 block">С:</label>
                                   <CustomDatePicker
                                       value={startDate}
                                       onChange={handleStartDateChange}
                                       className="w-full"
                                   />
                               </div>
                               <div className="w-1/2">
                                   <label className="text-xs text-gray-500 mb-1 block">По:</label>
                                   <CustomDatePicker
                                       value={endDate}
                                       onChange={handleEndDateChange}
                                       className="w-full"
                                   />
                               </div>
                           </div>
                         )}
                     </div>
                     <div className="w-auto">
                         <label className="form-label block mb-2">Норма публикаций:</label>
                         <input 
                             type="number" 
                             min="1" 
                             value={postThreshold} 
                             onChange={(e) => handlePostThresholdChange(e.target.value)}
                             className="form-control"
                             style={{ width: '100px' }}
                         />
                     </div>
                     <div className="w-auto min-w-[320px]">
                         <label className="form-label block mb-2">Режим анализа:</label>
                         <div className="mode-toggle" role="tablist" aria-label="Режим анализа">
                           <button
                             type="button"
                             role="tab"
                             aria-selected={analysisMode === 'single_period'}
                             className={`mode-toggle-button ${analysisMode === 'single_period' ? 'active' : ''}`}
                             onClick={() => handleAnalysisModeChange('single_period')}
                           >
                             Один выбранный период
                           </button>
                           <button
                             type="button"
                             role="tab"
                             aria-selected={analysisMode === 'monthly_4weeks'}
                             className={`mode-toggle-button ${analysisMode === 'monthly_4weeks' ? 'active' : ''}`}
                             onClick={() => handleAnalysisModeChange('monthly_4weeks')}
                           >
                             Месячная сводка по 4 неделям
                           </button>
                         </div>
                     </div>
                 </div>
                 {analysisMode === 'monthly_4weeks' && (
                   <div className="text-sm text-gray-600 mb-4">
                     Для месячной сводки достаточно выбрать месяц. Сервис проверит окна 1-7, 8-14, 15-21 и 22-28,
                     а дни после 28 числа учитывать не будет. Если выбрать текущий месяц раньше 29 числа по Москве,
                     анализ не запустится.
                   </div>
                 )}
                 <div className="flex items-center gap-3 mt-1">
                   <button
                       onClick={fetchDataFromVK}
                       disabled={isLoading || !foundersFile}
                       className={`btn ${isLoading || !foundersFile ? 'btn-disabled' : 'btn-primary'} flex items-center justify-center min-w-[200px]`}
                   >
                       {isLoading ? (
                         <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                           <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                           <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                         </svg>
                       ) : (
                       <svg width="22" height="22" viewBox="0 0 100 100" fill="none" stroke="none" className="mr-2">
                             <g><path d="M53.2085 72.042C30.4168 72.042 17.4169 56.417 16.8752 30.417H28.2919C28.6669 49.5003 37.0834 57.5836 43.75 59.2503V30.417H54.5004V46.8752C61.0837 46.1669 67.9995 38.667 70.3329 30.417H81.0832C79.2915 40.5837 71.7915 48.0836 66.4582 51.1669C71.7915 53.6669 80.3336 60.2086 83.5836 72.042H71.7499C69.2082 64.1253 62.8754 58.0003 54.5004 57.1669V72.042H53.2085Z" fill="currentColor"/>
                           </g>
                       </svg>
                       )}
                       {isLoading ? 'Загрузка данных...' : 'Начать анализ'}
                   </button>
                   {isLoading && (
                     <button
                       onClick={handleStopAnalysis}
                       className="btn btn-danger flex items-center justify-center min-w-[150px]"
                     >
                       <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2">
                         <circle cx="12" cy="12" r="10"></circle>
                         <rect x="9" y="9" width="6" height="6"></rect>
                       </svg>
                       Остановить
                     </button>
                   )}
                 </div>
             </div>
         </div>
          
          {isLoading && (
            <AnalysisProgressDisplay
              progress={progress}
              processingStage={processingStage}
              stageDetails={stageDetails}
              estimatedTimeLeft={estimatedTimeLeft}
              elapsedTime={elapsedTime}
              processingSpeed={processingSpeed}
              currentItem={currentItem}
            />
          )}
          
          {(statusMessage && (statusMessage.includes('завершен') || statusMessage.includes('Ошибка') || statusMessage.includes('остановлен'))) && (
            <div className={`mt-3 p-3 rounded flex items-start ${statusMessage.includes('Ошибка') ? 'bg-red-50 text-red-700' : (statusMessage.includes('остановлен') ? 'bg-yellow-50 text-yellow-700' : 'bg-green-50 text-green-700')}`}>
              {statusMessage.includes('Ошибка') && (
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 flex-shrink-0">
                  <circle cx="12" cy="12" r="10"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              )}
              {!statusMessage.includes('Ошибка') && !statusMessage.includes('остановлен') && (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 flex-shrink-0">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              )}
              {statusMessage.includes('остановлен') && (
                 <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 flex-shrink-0">
                   <circle cx="12" cy="12" r="10"></circle>
                   <polygon points="10 8 16 12 10 16 10 8"></polygon>
                 </svg> 
              )}
              <span className="leading-tight">{statusMessage}</span>
            </div>
          )}
        </div>

        {analysisError && (
          <div className="alert alert-error">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="mr-2 flex-shrink-0">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>
            <span><strong>Ошибка:</strong> {analysisError}</span>
          </div>
        )}

        {vkAnalysisResult && (
          <div className="card mt-6">
            {/* --- Контейнер для заголовка и кнопки "Скачать все" --- */}
            <div className="flex items-center justify-between px-4 pt-3 pb-4 border-b">
              <h2 className="text-xl font-medium pl-2">
                Результаты анализа
              </h2>
              {(processedOmsuForUI || processedIogvForUI) && (
                <button
                  onClick={handleExportAll}
                  className="btn btn-secondary flex items-center gap-2 px-4 py-1 mr-2 h-9"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 14.899A7 7 0 1 1 15.71 8h1.79a4.5 4.5 0 0 1 2.5 8.242" /><path d="M12 12v9" /><path d="m8 17 4 4 4-4" /></svg>
                  <span className="hidden sm:inline">Скачать все материалы</span>
                  <span className="sm:hidden">Всё</span> 
                </button>
              )}
            </div>
            
            {/* Добавляем отображение индикатора прогресса экспорта */}
            <ExportStatusIndicator exportStatus={exportStatus} />

            {/* --- Секция ОМСУ --- */}
            {processedOmsuForUI && processedOmsuForUI.hasData && (
              <div className="card-section section-content">
                 <ResponsiveComplianceChart 
                  processedData={processedOmsuForUI} 
                  chartType="omsu" 
                 />
                <div className="flex flex-wrap gap-3 justify-center mt-6">
                  <button
                    onClick={handleExportOmsuExcel}
                    className="btn btn-primary btn-sm sm:btn-md whitespace-nowrap"
                  >
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-1 flex-shrink-0"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M12 17v-6" /><path d="M9 14l3 3 3-3" /></svg>
                    <span className="sm:hidden">Excel</span>
                    <span className="hidden sm:inline">Данные ОМСУ</span>
                  </button>
                  <button
                    onClick={exportOmsuChart}
                    className="btn btn-success btn-sm sm:btn-md whitespace-nowrap"
                  >
                     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-1 flex-shrink-0"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M20.4 14.5L16 10 4 20" /></svg>
                     <span className="sm:hidden">PNG</span>
                     <span className="hidden sm:inline">График ОМСУ</span>
                  </button>
                </div>
              </div>
            )}

            {/* --- Секция ИОМО --- */}
            {processedIogvForUI && processedIogvForUI.hasData && (
              <div className="card-section section-content mt-6">
                 <ResponsiveComplianceChart 
                  processedData={processedIogvForUI} 
                  chartType="iogv"
                 />
                <div className="flex flex-wrap gap-3 justify-center mt-6">
                  <button
                    onClick={handleExportIogvExcel}
                    className="btn btn-primary btn-sm sm:btn-md whitespace-nowrap"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-1 flex-shrink-0"><path d="M14 3v4a1 1 0 0 0 1 1h4" /><path d="M17 21H7a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h7l5 5v11a2 2 0 0 1-2 2z" /><path d="M12 17v-6" /><path d="M9 14l3 3 3-3" /></svg>
                    <span className="sm:hidden">Excel</span>
                    <span className="hidden sm:inline">Данные ИОМО</span>
                  </button>
                  <button
                    onClick={exportIogvChart}
                    className="btn btn-success btn-sm sm:btn-md whitespace-nowrap"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="mr-1 flex-shrink-0"><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="8.5" cy="8.5" r="1.5" /><path d="M20.4 14.5L16 10 4 20" /></svg>
                    <span className="sm:hidden">PNG</span>
                    <span className="hidden sm:inline">График ИОМО</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default App;
